import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const audienceLabel = (it) => {
  if (it.kind === "meeting") return "Board";
  const a = it.audience_type || "everyone";
  if (a === "everyone") return "Everyone";
  if (a === "board") return "Board only";
  if (a === "tag") return it.audience_tag ? `#${it.audience_tag}` : "Tagged";
  return "";
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtTime = (s) => { try { return new Date(s).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const fmtDay = (s) => { try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }); } catch { return s; } };

function EventSlideOver({ item, isStaff, onClose, onDone }) {
  const rsvp = async (response) => { try { await api.post(`/events/${item.id}/rsvp`, { response }); toast.success("RSVP saved"); onDone && onDone(); onClose(); } catch (e) { toast.error(e.response?.data?.detail || "Could not RSVP"); } };
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-ink/50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-md bg-warm-cream h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="calendar-event-detail">
        <div className="sticky top-0 bg-ink text-cream px-6 py-4 flex items-center justify-between"><div className="font-zilla text-lg font-semibold truncate">{item.title}</div><button onClick={onClose} data-testid="calendar-event-close" className="w-8 h-8 rounded-full bg-cream/10 hover:bg-cream/20 flex items-center justify-center shrink-0"><Icon name="X" className="w-4 h-4" /></button></div>
        <div className="p-6 space-y-3">
          <div className="text-[14px] text-ink/70 flex items-center gap-2"><Icon name="Calendar" className="w-4 h-4 text-maroon" />{new Date(item.start).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
          {isStaff && <div className="flex items-center gap-2 flex-wrap"><span className="text-[12px] px-2 py-0.5 rounded-full bg-ink/10 font-semibold">Visible to: {audienceLabel(item)}</span>{item.status === "draft" && <span className="text-[12px] px-2 py-0.5 rounded-full font-semibold border border-dashed border-ink/40 text-ink/60">Draft — not visible to members yet</span>}</div>}
          {item.address && <div className="text-[14px] text-ink/70 flex items-center gap-2"><Icon name="MapPin" className="w-4 h-4 text-maroon" />{item.address}</div>}
          {item.description && <p className="text-[15px] whitespace-pre-wrap">{item.description}</p>}
          {item.video_link && <a href={item.video_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-ink/20 rounded-lg px-3 py-2 text-[14px] font-semibold"><Icon name="Video" className="w-4 h-4 text-dark-olive" /> Join the video call</a>}
          <div className="flex gap-2 pt-2 border-t border-ink/8">
            <button data-testid="calendar-rsvp-going" onClick={() => rsvp("going")} className={`flex-1 rounded-lg px-3 py-2.5 min-h-[44px] text-[14px] font-semibold border ${item.my_rsvp === "going" ? "bg-olive text-ink border-olive" : "border-ink/25"}`}>I'm going</button>
            <button data-testid="calendar-rsvp-cant" onClick={() => rsvp("cant")} className={`flex-1 rounded-lg px-3 py-2.5 min-h-[44px] text-[14px] font-semibold border ${item.my_rsvp === "cant" ? "bg-ink text-cream border-ink" : "border-ink/25"}`}>Can't make it</button>
          </div>
        </div>
      </div>
    </div>, document.body);
}

export default function UnifiedCalendar({ onOpenMeeting }) {
  const { persona } = useAuth();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(iso(today));
  const [openEvent, setOpenEvent] = useState(null);
  const [isBoard, setIsBoard] = useState(false);
  const [isStaff, setIsStaff] = useState(false);

  const loadItems = useCallback(() => { api.get("/calendar", { params: persona ? { persona } : {} }).then((r) => { setItems(r.data.items || []); setIsBoard(!!r.data.is_board); setIsStaff(!!r.data.is_staff); }).catch(() => {}); }, [persona]);
  useEffect(() => { loadItems(); }, [loadItems]);

  // In the member layout (incl. Super Admin previewing as a member) the server already
  // returns exactly what that role sees (persona-aware), so we render items as-is.
  const staff = isStaff;
  const board = isBoard;

  const byDay = useMemo(() => {
    const map = {};
    for (const it of items) { (map[it.date] = map[it.date] || []).push(it); }
    return map;
  }, [items]);

  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first); start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  const dayItems = byDay[selected] || [];
  const openItem = (it) => { if (it.kind === "meeting") { onOpenMeeting && onOpenMeeting(it.id); } else { setOpenEvent(it); } };

  return (
    <div data-testid="unified-calendar">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
        <h1 className="font-zilla text-2xl sm:text-4xl font-semibold">Calendar</h1>
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="border border-ink/20 rounded-lg w-10 h-10 shrink-0 flex items-center justify-center"><Icon name="ChevronRight" className="w-5 h-5 rotate-180" /></button>
          <div className="font-zilla text-base sm:text-lg font-semibold flex-1 sm:w-44 text-center" data-testid="cal-month">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</div>
          <button data-testid="cal-next" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="border border-ink/20 rounded-lg w-10 h-10 shrink-0 flex items-center justify-center"><Icon name="ChevronRight" className="w-5 h-5" /></button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-3 text-[13px]">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-maroon" /> Event</span>
        {board && <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-dark-olive" /> Board Meeting</span>}
        {staff && <span className="inline-flex items-center gap-1.5 text-ink/55"><span className="w-3 h-3 rounded-sm border border-dashed border-ink/40 bg-warm-cream" /> Draft (not yet visible to members)</span>}
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden">
        <div className="grid grid-cols-7 border-b border-ink/10">
          {DOW.map((d) => <div key={d} className="text-center text-[12px] font-semibold text-ink/55 py-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((d, i) => {
            const key = iso(d);
            const inMonth = d.getMonth() === cursor.getMonth();
            const its = byDay[key] || [];
            const isToday = key === iso(today);
            const isSel = key === selected;
            return (
              <button key={i} data-testid={`cal-day-${key}`} onClick={() => setSelected(key)}
                className={`min-h-[84px] border-b border-r border-ink/8 p-1.5 text-left align-top ${inMonth ? "" : "bg-warm-cream/40 text-ink/35"} ${isSel ? "ring-2 ring-inset ring-maroon" : ""}`}>
                <div className={`text-[12px] font-semibold w-6 h-6 flex items-center justify-center rounded-full ${isToday ? "bg-ink text-cream" : ""}`}>{d.getDate()}</div>
                <div className="space-y-0.5 mt-0.5">
                  {its.slice(0, 3).map((it) => (
                    <div key={it.kind + it.id} onClick={(e) => { e.stopPropagation(); openItem(it); }}
                      data-testid={`cal-item-${it.id}`}
                      className={`truncate text-[11px] rounded px-1 py-0.5 cursor-pointer text-cream ${it.kind === "meeting" ? "bg-dark-olive" : "bg-maroon"} ${it.status === "draft" ? "opacity-45 italic" : ""}`}>{it.kind === "meeting" ? "▪ " : ""}{it.status === "draft" ? "Draft: " : ""}{it.title}</div>
                  ))}
                  {its.length > 3 && <div className="text-[10px] text-ink/50 px-1">+{its.length - 3} more</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5" data-testid="cal-day-list">
        <h2 className="font-zilla text-xl font-semibold mb-2">{fmtDay(selected)}</h2>
        {dayItems.length === 0 ? <div className="text-ink/55 text-[15px]">Nothing scheduled.</div> : (
          <div className="space-y-2">
            {dayItems.map((it) => (
              <button key={it.kind + it.id} data-testid={`cal-list-${it.id}`} onClick={() => openItem(it)}
                className="w-full text-left bg-cream border border-ink/15 rounded-xl px-4 py-3 flex items-center justify-between gap-3 hover:border-maroon/50">
                <div className="min-w-0">
                  <div className="text-[16px] font-semibold truncate">{it.title}</div>
                  <div className="text-[13px] text-ink/55">{it.kind === "meeting" ? "Board Meeting" : (it.start ? `Event · ${fmtTime(it.start)}` : "Event")}{staff && it.kind === "event" ? ` · ${audienceLabel(it)}` : ""}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {staff && it.status === "draft" && <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold border border-dashed border-ink/40 text-ink/60">Draft</span>}
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold text-cream ${it.kind === "meeting" ? "bg-dark-olive" : "bg-maroon"}`}>{it.kind === "meeting" ? "Meeting" : "Event"}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {openEvent && <EventSlideOver item={openEvent} isStaff={staff} onClose={() => setOpenEvent(null)} onDone={loadItems} />}
    </div>
  );
}
